import { MaterialIcons } from '@expo/vector-icons';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { useEffect, useRef, useState } from 'react';
import { Pressable, StyleSheet } from 'react-native';
import { ContextMenuAction } from 'react-native-context-menu-view';
import Animated, { Easing, runOnUI, useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';

import { ChatActions } from '@/constants/enums/ChatActions';

interface ContextMenuProps {
  children: React.ReactNode;
  disabled: boolean;
  actions: ContextMenuAction[] | undefined;
  longPressable?: boolean;
  showMenuPadding?: number;
  onPress: (e: ChatActions | string) => void;
}

const AnimatedPressable = Animated.createAnimatedComponent(Pressable);

const ContextMenu = ({
  children,
  disabled,
  actions,
  longPressable = true,
  showMenuPadding = 10,
  onPress
}: ContextMenuProps) => {
  const [showMenu, setShowMenu] = useState<boolean>(false);
  const showMenuVal = useSharedValue<boolean>(false);
  const contextRef = useRef<HTMLDivElement | null>(null);

  const handleOutsideClick = (e: MouseEvent) => {
    if (contextRef.current && !contextRef.current.contains(e.target as Node) && !disabled) {
      showMenuVal.value = false;
      setShowMenu(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  });

  //Animated styles
  const animatedPressableStyle = useAnimatedStyle(() => ({
    backgroundColor: withTiming(showMenuVal.value ? '#eeeeee' : 'white', {
      duration: 300,
      easing: Easing.inOut(Easing.poly(3))
    }),
    transform: [
      {
        scaleY: withTiming(showMenuVal.value ? 1.05 : 1, {
          duration: 300,
          easing: Easing.inOut(Easing.poly(3))
        })
      },
      {
        scaleX: withTiming(showMenuVal.value ? 1.04 : 1, {
          duration: 300,
          easing: Easing.inOut(Easing.poly(3))
        })
      }
    ],
    padding: withTiming(showMenuVal.value ? showMenuPadding : 0, {
      duration: 300,
      easing: Easing.inOut(Easing.poly(3))
    }),
    borderRadius: withTiming(showMenuVal.value ? 10 : 5, {
      duration: 400,
      easing: Easing.inOut(Easing.poly(3))
    })
  }));

  const animatedMenuStyle = useAnimatedStyle(() => ({
    opacity: withTiming(showMenuVal.value ? 1 : 0, { duration: 300 }),
    transform: [
      {
        translateY: withTiming(showMenuVal.value ? 0 : -10, {
          duration: 300,
          easing: Easing.out(Easing.poly(3))
        })
      }
    ]
  }));

  return (
    <div ref={contextRef} className="relative">
      <AnimatedPressable
        style={[styles.pressableMsg, animatedPressableStyle]}
        {...(longPressable
          ? {
              onLongPress: () => {
                runOnUI(() => {
                  'worklet';
                  showMenuVal.value = true;
                  setShowMenu(true);
                })();
              }
            }
          : {
              onPress: () => {
                runOnUI(() => {
                  'worklet';
                  showMenuVal.value = true;
                  setShowMenu(true);
                })();
              }
            })}
        disabled={disabled || !actions || actions.length === 0}>
        {children}
      </AnimatedPressable>

      {showMenu && actions && actions.length >= 1 && !disabled && (
        <Animated.View style={[styles.actionMenu, animatedMenuStyle]}>
          {actions.find(
            (action) =>
              action.title === ChatActions.RESPOND ||
              action.title === ChatActions.EDIT ||
              action.title === ChatActions.DELETE
          ) ? (
            <>
              {actions.some((action) => action.title === ChatActions.RESPOND) && !disabled && (
                <button className="flex hover:scale-110" onClick={() => onPress(ChatActions.RESPOND)}>
                  <MaterialCommunityIcons
                    style={styles.iconAction}
                    name="message-reply-text-outline"
                    color="gray"
                    size={24}
                  />
                </button>
              )}
              {actions.some((action) => action.title === ChatActions.DELETE) && !disabled && (
                <button className="flex hover:scale-110" onClick={() => onPress(ChatActions.DELETE)}>
                  <MaterialIcons style={styles.iconAction} name="delete-outline" color="gray" size={24} />
                </button>
              )}
              {actions.some((action) => action.title === ChatActions.EDIT) && !disabled && (
                <button className="flex hover:scale-110" onClick={() => onPress(ChatActions.EDIT)}>
                  <MaterialIcons style={styles.iconAction} name="edit" color="gray" size={24} />
                </button>
              )}
            </>
          ) : (
            actions.map((action) => (
              <button key={action.title} className="flex hover:scale-110" onClick={() => onPress(action.title)}>
                <MaterialIcons
                  style={styles.iconAction}
                  name={action.icon as 'edit' | 'delete' | 'repeat'}
                  color={action.iconColor}
                  size={24}
                />
              </button>
            ))
          )}
        </Animated.View>
      )}
    </div>
  );
};

export default ContextMenu;

const styles = StyleSheet.create({
  iconAction: {
    alignSelf: 'center'
  },

  pressableMsg: {
    display: 'flex',
    flexDirection: 'row',
    height: '100%',
    columnGap: 12
  },

  actionMenu: {
    position: 'absolute',
    top: -20,
    right: 0,
    zIndex: 2000,
    backgroundColor: 'white',
    padding: 8,
    borderRadius: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    display: 'flex',
    flexDirection: 'row'
  }
});
